/**
 * Regeln rund um Gold und Income.
 *
 * Alle Funktionen hier sind "pur": sie lesen nur ihre Argumente und geben ein
 * Ergebnis zurueck. Dadurch sind sie leicht automatisch testbar - und der
 * Server kann sie als einzige Wahrheitsquelle benutzen (Masterprompt Abschnitt 28).
 */

import {
  INCOME_TICK_SECONDS,
  KILL_GOLD_MINIMUM,
  KILL_GOLD_RATIO,
  TIER_UPGRADE_COST,
  getCreep,
  type CreepDefinition,
  type CreepTier,
} from '@dueline/game-data';

/** Summe der Goldkosten einer Welle. Unbekannte Creep-IDs werfen einen Fehler. */
export function calculateWaveCost(creepIds: readonly string[]): number {
  return creepIds.reduce((sum, id) => sum + requireCreep(id).cost, 0);
}

/** Dauerhafter Income-Zuwachs, den eine Welle dem Sender einbringt. */
export function calculateWaveIncomeGain(creepIds: readonly string[]): number {
  return creepIds.reduce((sum, id) => sum + requireCreep(id).incomeGain, 0);
}

/**
 * Gold, das der Verteidiger fuer das Toeten eines Creeps erhaelt:
 * 10 Prozent des Kaufpreises, mindestens 2 Gold (Masterprompt Abschnitt 63).
 */
export function calculateKillGold(creep: CreepDefinition): number {
  return Math.max(KILL_GOLD_MINIMUM, Math.floor(creep.cost * KILL_GOLD_RATIO));
}

/** Prueft, ob ein Betrag bezahlbar ist. Negative Preise sind immer ungueltig. */
export function canAfford(gold: number, price: number): boolean {
  return price >= 0 && gold >= price;
}

/** Kosten fuer den Aufstieg auf das naechste Tier. */
export function getTierUpgradeCost(targetTier: 2 | 3 | 4): number {
  return TIER_UPGRADE_COST[targetTier];
}

/**
 * Prueft, ob ein Tier-Upgrade zulaessig ist.
 * Tiers muessen der Reihe nach gekauft werden, es gibt aber keine Zeit-
 * oder Levelvoraussetzung (Tier Rush ist ausdruecklich erlaubt).
 */
export function canUpgradeTier(currentTier: CreepTier, gold: number): boolean {
  if (currentTier >= 4) return false;
  const targetTier = (currentTier + 1) as 2 | 3 | 4;
  return canAfford(gold, getTierUpgradeCost(targetTier));
}

/** Liefert die Creep-Definition oder wirft einen aussagekraeftigen Fehler. */
export function requireCreep(id: string): CreepDefinition {
  const creep = getCreep(id);
  if (!creep) {
    throw new Error(`Unbekannte Creep-ID: "${id}"`);
  }
  return creep;
}

/**
 * Anzahl der Income-Ausschuettungen, die bis zu diesem Zeitpunkt haetten
 * stattfinden muessen. Alle 15 Sekunden eine (Masterprompt Abschnitt 63).
 * Bei Sekunde 0 hat noch keine stattgefunden.
 */
export function getIncomeTicksElapsed(matchSeconds: number): number {
  if (matchSeconds < INCOME_TICK_SECONDS) return 0;
  return Math.floor(matchSeconds / INCOME_TICK_SECONDS);
}

/** Sekunden bis zur naechsten Income-Ausschuettung - fuer die HUD-Anzeige. */
export function getSecondsUntilNextIncomeTick(matchSeconds: number): number {
  const remainder = matchSeconds % INCOME_TICK_SECONDS;
  return remainder === 0 ? INCOME_TICK_SECONDS : INCOME_TICK_SECONDS - remainder;
}

/** Wendet eine Income-Ausschuettung an und gibt den neuen Goldstand zurueck. */
export function applyIncomeTick(gold: number, income: number): number {
  return gold + Math.max(0, income);
}

/**
 * Income nach dem Senden einer Welle.
 * Die Erhoehung gilt sofort, also bereits fuer den naechsten Tick.
 */
export function applyWaveIncomeGain(income: number, creepIds: readonly string[]): number {
  return income + calculateWaveIncomeGain(creepIds);
}

/** Ergebnis einer Income-Auszahlung. */
export interface IncomePayout {
  /** Neuer Goldstand. */
  gold: number;
  /** Anzahl der insgesamt ausgezahlten Ticks. */
  ticksPaid: number;
  /** In diesem Aufruf ausgezahltes Gold. */
  paid: number;
}

/**
 * Zahlt alle faelligen Income-Ticks aus (Masterprompt Abschnitt 63).
 *
 * Statt "alle 15 Sekunden einmal" zu zaehlen, wird hier gefragt: Wie viele Ticks
 * haetten bis jetzt stattfinden muessen, und wie viele wurden schon bezahlt?
 * Die Differenz wird ausgezahlt. Dadurch geht auch bei einem ruckelnden Takt
 * nie ein Tick verloren, und keiner wird doppelt bezahlt.
 *
 * @param matchSeconds Verstrichene KAMPFZEIT. Waehrend Vorbereitung und
 *   Wartezeit steht sie still - dann gibt es auch kein Income.
 */
export function payDueIncome(
  gold: number,
  income: number,
  ticksPaid: number,
  matchSeconds: number,
): IncomePayout {
  const faellig = getIncomeTicksElapsed(matchSeconds);
  if (faellig <= ticksPaid) return { gold, ticksPaid, paid: 0 };

  let neuesGold = gold;
  for (let i = ticksPaid; i < faellig; i += 1) {
    neuesGold = applyIncomeTick(neuesGold, income);
  }
  return { gold: neuesGold, ticksPaid: faellig, paid: neuesGold - gold };
}
